import type { FC, ReactNode } from 'react'
import type { JournalPostSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import Img from '@/components/generals/img'

interface StoryRowProps {
    post: JournalPostSummary
    index: number
}

/** A journal index row: topic, title and read time, with a thumbnail that opens up on hover. */
const StoryRow: FC<StoryRowProps> = ({ post, index }): ReactNode => {
    return (
        <li data-reveal='up' className='border-b border-line'>
            <Link
                href={`/journal/${post.slug}`}
                data-transition-label={post.title}
                className='group relative grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-3 py-6 transition-colors duration-500 md:grid-cols-12 md:py-8'
            >
                <span aria-hidden='true' className='pointer-events-none absolute inset-y-0 -left-[var(--gutter)] -right-[var(--gutter)] origin-bottom scale-y-0 bg-coal transition-transform duration-700 ease-expo-out group-hover:scale-y-100' />

                <span className='relative col-span-2 flex items-center gap-4 meta-mono md:col-span-2'>
                    <span className='text-ash'>
                        {String(index + 2).padStart(2, '0')}
                    </span>

                    <span className='text-acid' data-mdlfy-cms={`journal_posts:${post.id}:category`}>{post.category}</span>
                </span>

                <span className='relative font-heading text-[clamp(30px,3.6vw,56px)] uppercase leading-[0.95] text-bone transition-transform duration-700 ease-expo-out group-hover:translate-x-3 md:col-span-7'>
                    <span data-mdlfy-cms={`journal_posts:${post.id}:title`}>{post.title}</span>
                </span>

                <span className='relative hidden items-center gap-3 meta-mono text-ash md:col-span-1 md:flex'>
                    {post.read_time ? <span data-mdlfy-cms={`journal_posts:${post.id}:read_time`}>{post.read_time}</span> : null}
                </span>

                <span className='relative flex items-center justify-end gap-4 md:col-span-2'>
                    <span className='relative block aspect-[4/5] w-16 overflow-hidden bg-ink transition-[width] duration-700 ease-expo-out md:w-24 md:group-hover:w-32'>
                        <Img
                            src={post.cover}
                            alt={post.cover_alt || post.title}
                            sizes='128px'
                            className='transition-transform duration-[1.2s] ease-expo-out group-hover:scale-110'
                        />
                    </span>

                    <ArrowUpRight aria-hidden='true' className='hidden size-5 text-bone transition-transform duration-500 ease-expo-out group-hover:rotate-45 md:block' />
                </span>
            </Link>
        </li>
    )
}

export default StoryRow
